import dayjs from 'dayjs'

import { AlertGridCell } from '@/shared/components/GridTable/GridCells/AlertGridCell'
import { ColourGridCell } from '@/shared/components/GridTable/GridCells/ColourGridCell'
import { StopTypeGridCell } from '@/shared/components/GridTable/GridCells/StopTypeGridCell'
import { GridColDef } from '@mui/x-data-grid-premium'

import { ColumnDefinition } from './models/ColumnDefinition'

export type ColumnDefinitionMap = {
  [key: ColumnDefinition['name']]: GridColDef
}

export const commonGridColDefs: ColumnDefinitionMap = {
  geocodeError: {
    field: 'geocodeError',
    headerName: '',
    width: 28,
    minWidth: 28,
    filterable: false,
    sortable: false,
    pinnable: false,
    renderCell: (params) => (<AlertGridCell {...params} />), 
  },
  stopType: {
    field: 'displayStopType',
    headerName: 'Type',
    width: 98,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
    renderCell: (params) => (<StopTypeGridCell {...params} />), 
  },
  orderNumber: {
    field: 'orderNumber',
    headerName: 'Number',
    width: 110,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  sequence: {
    field: 'sequence',
    headerName: 'Seq',
    width: 50,
    minWidth: 50,
    filterable: false,
    sortable: false,
    pinnable: false,
    type: 'number',
    valueFormatter: (value) => value === 0 ? '' : value,
  },
  preferredDateOfOperation: {
    field: 'preferredDateOfOperation',
    headerName: 'Date',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'date',
    valueGetter: (value: string, _) => {
      if (!value) return ''

      return dayjs(value.trim(), 'DD/MM/YYYY').isValid() ? dayjs(value, 'DD/MM/YYYY').toDate() : ''
    },
  },
  orderType: {
    field: 'orderType',
    headerName: 'Type',
    width: 70,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    valueGetter: (_, row) => row.isPickup ? 'Pickup' : 'Delivery',
  },
  orderQuantity: {
    field: 'orderQuantity',
    headerName: 'Qty',
    width: 72,
    minWidth: 72,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
    valueFormatter: (value) => value === 0 ? '' : value,
  },
  weight: {
    field: 'weight',
    headerName: 'Weight',
    width: 72,
    minWidth: 72,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
    valueFormatter: (value) => value === 0 ? '' : value,
  },
  volume: {
    field: 'volume',
    headerName: 'Volume',
    width: 72,
    minWidth: 72,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
    valueFormatter: (value) => value === 0 ? '' : value,
  },
  maxOnboardTime: {
    field: 'maxOnboardTime',
    headerName: 'Max Onboard Time',
    width: 72,
    minWidth: 72,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
  },
  portalClientName: {
    field: 'portalClientName',
    headerName: 'Portal Client Name',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  timeWindow: {
    field: 'timeWindow',
    headerName: 'Time Windows',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueGetter: (_, row) => row.timeWindows?.map((tw: { display: any }) => tw.display).join(', '),
  },
  serviceTime: {
    field: 'serviceTime',
    headerName: 'Site Service Time',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  priority: {
    field: 'priority',
    headerName: 'Priority',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  displayOrderAttributes: {
    field: 'displayOrderAttributes',
    headerName: 'Attributes',
    width: 120,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  tripNo: {
    field: 'tripNumber',
    headerName: 'Trip No.',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  consignmentNumber: {
    field: 'consignmentNumber',
    headerName: 'Consig. No.',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  requested: {
    field: 'requested',
    headerName: 'Requested',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  description: {
    field: 'description',
    headerName: 'Description',
    width: 200,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryInstruction: {
    field: 'deliveryInstruction',
    headerName: 'Delivery Instructions',
    width: 200,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  comments: {
    field: 'comments',
    headerName: 'Comments',
    width: 200,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  customerAddress: {
    field: 'customerAddress',
    headerName: 'Address',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  customerSuburb: {
    field: 'customerSuburb',
    headerName: 'Suburb',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  customerState: {
    field: 'customerState',
    headerName: 'State',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  customerPostcode: {
    field: 'customerPostcode',
    headerName: 'Postcode',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  customerMapRef: {
    field: 'customerMapRef',
    headerName: 'Map Ref.',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  customerAdditional: {
    field: 'customerAdditional',
    headerName: 'Additional',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  customerNumber: {
    field: 'customerNumber',
    headerName: 'Number',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
  },
  customerName: {
    field: 'customerName',
    headerName: 'Name',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  depotCode: {
    field: 'depotCode',
    headerName: 'Code',
    width: 75,
    minWidth: 75,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  depotName: {
    field: 'depotName',
    headerName: 'Name',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  stopCompartment: {
    field: 'stopCompartment',
    headerName: 'Stop Compartment',
    width: 70,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  eta: {
    // This field seems to be a grouping
    field: 'eta',
    headerName: 'ETA',
    width: 70,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  etaTime: {
    field: 'etaTime',
    headerName: 'PTA', // Planned Time Arrival
    width: 70,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  adtTime: {
    field: 'adtTime',
    headerName: 'ATA',
    width: 70,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  timeAtStop: {
    field: 'timeAtStop',
    headerName: 'Time At Stop',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  transitDistance: {
    field: 'transitDistance',
    headerName: 'Transit Distance',
    width: 120,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
  },
  transitTime: {
    field: 'transitTime',
    headerName: 'Transit Time',
    width: 120,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  runningQuantity: {
    field: 'runningQuantity',
    headerName: 'Quantity',
    width: 75,
    minWidth: 75,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
  },
  runningWeight: {
    field: 'runningWeight',
    headerName: 'Weight',
    width: 75,
    minWidth: 75,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
  },
  runningVolume: {
    field: 'runningVolume',
    headerName: 'Volume',
    width: 75,
    minWidth: 75,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
  },
  runningDistance: {
    field: 'runningDistance',
    headerName: 'Distance',
    width: 75,
    minWidth: 75,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'number',
  },
  runningWorkingTime: {
    field: 'runningWorkingTime',
    headerName: 'Time',
    width: 75,
    minWidth: 75,
    filterable: false,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  driverNote: {
    field: 'driverNote',
    headerName: 'Driver Note',
    width: 200,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  statusType: {
    field: 'statusType',
    headerName: 'Status',
    width: 140,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
    renderCell: (params) => (<ColourGridCell {...params} />),
    valueGetter: (value, row) => {
      return {
        text: value,
        cellStyle: row.statusCellStyle,
      }
    },
  },
  colorCode: {
    field: 'colorCode',
    headerName: 'Colour Code',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupName: {
    field: 'pickupName',
    headerName: 'Name',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupExternalId: {
    field: 'pickupExternalId',
    headerName: 'Ref. No',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupAddress: {
    field: 'pickupAddress',
    headerName: 'Address',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupSuburb: {
    field: 'pickupSuburb',
    headerName: 'Suburb',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupState: {
    field: 'pickupState',
    headerName: 'State',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupPostcode: {
    field: 'pickupPostcode',
    headerName: 'Postcode',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupMapRef: {
    field: 'pickupMapRef',
    headerName: 'Map Ref.',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupAdditional: {
    field: 'pickupAdditional',
    headerName: 'Additional',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupTimeWindow: {
    field: 'pickupTimeWindow',
    headerName: 'Pickup TW',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupServiceTime: {
    field: 'pickupServiceTime',
    headerName: 'Service Time',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  pickupInstruction: {
    field: 'pickupInstruction',
    headerName: 'Pickup Instructions',
    width: 200,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryName: {
    field: 'deliveryName',
    headerName: 'Name',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryExternalId: {
    field: 'deliveryExternalId',
    headerName: 'Ref. No',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryAddress: {
    field: 'deliveryAddress',
    headerName: 'Address',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliverySuburb: {
    field: 'deliverySuburb',
    headerName: 'Suburb',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryState: {
    field: 'deliveryState',
    headerName: 'State',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryPostcode: {
    field: 'deliveryPostcode',
    headerName: 'Postcode',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryMapRef: {
    field: 'deliveryMapRef',
    headerName: 'Map Ref.',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryAdditional: {
    field: 'deliveryAdditional',
    headerName: 'Additional',
    width: 100,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryTimeWindow: {
    field: 'deliveryTimeWindow',
    headerName: 'Delivery TW',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
  deliveryServiceTime: {
    field: 'deliveryServiceTime',
    headerName: 'Service Time',
    width: 150,
    minWidth: 40,
    filterable: true,
    sortable: false,
    pinnable: false,
    type: 'string',
  },
}
